import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner } from '@react-apps/design-system';
import { useImpersonationStore } from './impersonation.store';
import { loadTargetRoles } from './load-target-roles';
import { endImpersonationSession } from './impersonation-api';

const timeFormat = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Faixa no topo (vermelha — mais forte que o "visualizar como" âmbar, porque isto
 * muda a identidade no backend) avisando que o admin está atuando como outro
 * usuário, e até quando, com saída em um clique.
 *
 * Fica montada o tempo todo no header, e por isso é ela quem:
 * - busca as roles do alvo para o menu, inclusive depois de um reload;
 * - derruba a máscara no `expiresAt`, sem esperar a próxima request voltar 401;
 * - limpa o cache do TanStack Query sempre que a máscara cai (saída, 401 de
 *   sessão encerrada, prazo), para os dados voltarem com a identidade real.
 */
export function ImpersonationBanner() {
  const target = useImpersonationStore((s) => s.target);
  const sessionId = useImpersonationStore((s) => s.sessionId);
  const expiresAt = useImpersonationStore((s) => s.expiresAt);
  const stopImpersonation = useImpersonationStore((s) => s.stopImpersonation);
  const queryClient = useQueryClient();
  const [exiting, setExiting] = useState(false);
  /** `expiresAt` da sessão que saiu da tela sem ser encerrada no backend. */
  const [unendedUntil, setUnendedUntil] = useState<string | null>(null);
  const targetUserId = target?.userId;

  useEffect(() => {
    if (targetUserId) void loadTargetRoles();
  }, [targetUserId]);

  const previousSessionId = useRef(sessionId);
  useEffect(() => {
    if (previousSessionId.current && previousSessionId.current !== sessionId) {
      queryClient.clear();
    }
    // Uma máscara nova invalida o aviso da anterior.
    if (sessionId) setUnendedUntil(null);
    previousSessionId.current = sessionId;
  }, [sessionId, queryClient]);

  useEffect(() => {
    if (!expiresAt) return;
    const timer = setTimeout(
      stopImpersonation,
      Math.max(0, new Date(expiresAt).getTime() - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [expiresAt, stopImpersonation]);

  // Passado o prazo, o aviso de sessão não encerrada já não é verdade.
  useEffect(() => {
    if (!unendedUntil) return;
    const timer = setTimeout(
      () => setUnendedUntil(null),
      Math.max(0, new Date(unendedUntil).getTime() - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [unendedUntil]);

  if (!target || !sessionId || !expiresAt) {
    if (!unendedUntil) return null;
    return (
      <Banner
        tone="warning"
        actionLabel="Fechar"
        onAction={() => setUnendedUntil(null)}
      >
        Você saiu da máscara, mas não foi possível encerrar a sessão de suporte
        no servidor. Ela expira sozinha às{' '}
        {timeFormat.format(new Date(unendedUntil))}.
      </Banner>
    );
  }

  // A sessão se encerra no backend antes de a máscara cair aqui. Se a chamada
  // falhar, a máscara cai do mesmo jeito — deixar o operador preso nela seria
  // pior —, mas o aviso diz que a sessão segue valendo até o prazo.
  const handleExit = async () => {
    setExiting(true);
    setUnendedUntil(null);
    try {
      await endImpersonationSession(sessionId);
    } catch {
      setUnendedUntil(expiresAt);
    } finally {
      setExiting(false);
      stopImpersonation();
    }
  };

  return (
    <Banner
      tone="danger"
      actionLabel={exiting ? 'Saindo…' : 'Sair da máscara'}
      onAction={() => void handleExit()}
      actionDisabled={exiting}
    >
      Atuando como <strong>{target.name}</strong> até{' '}
      {timeFormat.format(new Date(expiresAt))} — toda requisição usa a
      identidade dessa pessoa no backend.
    </Banner>
  );
}
